import React from "react";
import { RouterProvider } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./services/queryClient";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ToastProvider } from "./contexts/ToastContext";
import pushNotificationService from "./services/pushNotificationService";
import SessionManager from "./utils/sessionManager";
import { monitoring } from "./services/monitoring";
import { logger } from "./utils/logger";
import { isSupabaseAuthDomain } from "./utils/runtimeEnvironment";
import { router } from "./config/routes";

function App() {
  // Initialize security services on app start
  React.useEffect(() => {
    if (isSupabaseAuthDomain()) {
      logger.debug("Running inside Supabase auth domain - skipping monitoring and push init",
      );
      return;
    }

    // Initialize monitoring services asynchronously (Sentry, GA, Mixpanel)
    // Sentry is loaded dynamically AFTER React is ready to prevent circular dependencies
    const initMonitoring = async () => {
      try {
        await monitoring.init();
        logger.debug("Monitoring services initialized");
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("Failed to initialize monitoring:", error);
        }
        logger.error("Failed to initialize monitoring", { error });
      }
    };

    initMonitoring();

    // Initialize session manager for timeout and activity tracking
    SessionManager.init();
    logger.debug("Session manager initialized");

    // Handle auth redirect events from API interceptor
    const handleAuthRedirect = (event: CustomEvent) => {
      const { path } = event.detail;
      // Use React Router navigation instead of page reload
      if (path === "/login") {
        // Force a proper navigation to login page
        window.location.replace("/login");
      }
    };

    window.addEventListener(
      "auth-redirect",
      handleAuthRedirect as EventListener,
    );

    // Initialize push notifications in the background
    // This will register the service worker and check existing subscriptions
    // No need to await as it's a background initialization
    const initPushNotifications = async () => {
      try {
        const initialized = await pushNotificationService.initialize();
        if (initialized) {
          logger.debug("Push notification service initialized");

          // Check if user was previously subscribed and restore subscription
          const isSubscribed = await pushNotificationService.isSubscribed();
          if (isSubscribed) {
            logger.debug("User has active push subscription");
          }
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("Failed to initialize push notifications:", error);
        }
        logger.error("Failed to initialize push notifications", { error });
      }
    };

    initPushNotifications();

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener(
        "auth-redirect",
        handleAuthRedirect as EventListener,
      );
    };
  }, []);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider
            router={router}
            future={{
              v7_startTransition: true,
            }}
          />
          <Analytics />
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
